import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { handleDomBubbleEvent } from '@/utils/dom';
import { useUserModalRootActionsStore } from '@/features/users/_shared/components/user-modal-root/user-modal-root.store';
import {
  DeleteAgentOnTeamSchema,
  TDeleteAgentOnTeamValidation,
  useDeleteAgentOnTeam,
} from '../../../api/delete-agent-on-team.api';
import { GET_LIST_AGENT_QUERY_KEY } from '@/features/users/agent/api/get-list-agent-api';
import { GET_DETAIL_AGENT_QUERY_KEY } from '@/features/users/agent/api/get-detail-agent.api';
import { GET_LIST_TEAM_QUERY_KEY } from '../../../api/get-list-team-api';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import type { AxiosErrorCustom } from '@/lib/axios';

// ------------------------------------------------------------------------
const TOAST_MESSAGES_DELETE_AGENT_ON_TEAM = {
  DELETE_AGENT_ON_TEAM_ERROR: 'Remove agent from team failed!',
  DELETE_AGENT_ON_TEAM_SUCCESS: 'Agent removed from team successfully!',
};

export type TDeleteAgentOnTeamModalType = 'DeleteAgentOnTeamModal';
export type IDeleteAgentOnTeamModalProps = {
  teamId: string;
  userIds: string[];
};

const DeleteAgentFromTeamModal = memo(({ teamId, userIds }: IDeleteAgentOnTeamModalProps) => {
  const userModalRootActions = useUserModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteAgentOnTeamModal = useMemo(() => {
    return userModalRootActions.checkIsOpenModal('DeleteAgentOnTeamModal');
  }, [userModalRootActions]);

  const form = useForm<TDeleteAgentOnTeamValidation>({
    mode: 'onChange',
    resolver: zodResolver(DeleteAgentOnTeamSchema),
  });

  useEffect(() => {
    if (teamId) {
      form.reset({
        teamId,
        userIds,
      });
    }
  }, [teamId, userIds, form]);

  const mutationDeleteAgentOnTeam = useDeleteAgentOnTeam({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage =
          axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_AGENT_ON_TEAM.DELETE_AGENT_ON_TEAM_ERROR;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        const currentTeamId = form.getValues('teamId');
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [GET_LIST_AGENT_QUERY_KEY, { teamId: currentTeamId }] }),
          queryClient.invalidateQueries({ queryKey: [GET_LIST_AGENT_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_DETAIL_AGENT_QUERY_KEY, currentTeamId] }),
          queryClient.invalidateQueries({ queryKey: [GET_LIST_TEAM_QUERY_KEY] }),
        ]);
        form.reset();
        userModalRootActions.closeModal('DeleteAgentOnTeamModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_AGENT_ON_TEAM.DELETE_AGENT_ON_TEAM_SUCCESS });
      },
    },
  });

  const onSubmit = useCallback(
    (data: TDeleteAgentOnTeamValidation) => {
      if (data.teamId && data.userIds) {
        mutationDeleteAgentOnTeam.mutate({
          teamId: data.teamId,
          userIds: data.userIds,
        });
      }
    },
    [mutationDeleteAgentOnTeam]
  );

  const handleClose = useCallback(() => {
    form.reset();
    userModalRootActions.closeModal('DeleteAgentOnTeamModal');
  }, [form, userModalRootActions]);

  return (
    <Modal isOpen={isOpenDeleteAgentOnTeamModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Remove Agent from Team</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(onSubmit)(e);
          }}
          id="delete-agent-on-team-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">
            Are you sure you want to remove this agent from the team ? <br /> This action will not delete the agent from
            the system
          </span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          form="delete-agent-on-team-form"
          type="submit"
          disabled={mutationDeleteAgentOnTeam.isPending}
        >
          {mutationDeleteAgentOnTeam.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}{' '}
          Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

DeleteAgentFromTeamModal.displayName = 'DeleteAgentFromTeamModal';
export default DeleteAgentFromTeamModal;
